import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  CameraIcon,
  CircleMinusIcon,
  ClipboardCheckIcon,
  MoveIcon,
  PackageCheckIcon,
  PrinterIcon,
  SplineIcon,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import ClickableTable from '@/components/ui/ClickableTable.tsx';
import usePatientStore from '@patientMenu/hooks/usePatientStore.ts';
import { usePatients } from '@hooks/usePatient.ts';
import { PatientResponse } from '@schema/patient.ts';
import { PageResponse } from '@/schema/page';
import { Status } from '@/schema/jawsScan';

export function PatientsTable({ className }: { className?: string }) {
  const { selectedPatients, setSelectedPatients, query, setPageable, pageable } = usePatientStore();
  const { patientId } = useParams<{ patientId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const patientsQuery = usePatients(query, pageable);
  const [latestPage, setLatestPage] = useState<PageResponse<PatientResponse> | null>(null);

  useEffect(() => {
    if (patientsQuery.data) {
      setLatestPage(patientsQuery.data);
    }
  }, [patientsQuery.data]);

  const tablePage = patientsQuery.data ?? latestPage;
  const tableData = tablePage?.content ?? [];
  const totalElements = tablePage?.totalElements ?? 0;

  type PatientRow = PatientResponse & { fullName: string };

  const rows: PatientRow[] = useMemo(
    () =>
      tableData.map((patient) => ({
        ...patient,
        fullName: `${patient.firstName} ${patient.lastName}`.trim(),
      })),
    [tableData],
  );

  const STATUS_DETAILS: Record<Exclude<Status, null>, { label: string; icon: LucideIcon; className: string }> = {
    [Status.scanned]: {
      label: 'Scanned',
      icon: CameraIcon,
      className: 'border-blue-500/40 bg-blue-500/15 text-blue-200',
    },
    [Status.segmented]: {
      label: 'Segmented',
      icon: SplineIcon,
      className: 'border-purple-500/40 bg-purple-500/15 text-purple-200',
    },
    [Status.corrected]: {
      label: 'Corrected',
      icon: MoveIcon,
      className: 'border-amber-500/40 bg-amber-500/15 text-amber-200',
    },
    [Status.printing]: {
      label: 'Printing',
      icon: PrinterIcon,
      className: 'border-orange-500/40 bg-orange-500/15 text-orange-200',
    },
    [Status.braces_created]: {
      label: 'Braces Created',
      icon: ClipboardCheckIcon,
      className: 'border-emerald-500/40 bg-emerald-500/15 text-emerald-200',
    },
    [Status.printed]: {
      label: 'Printed',
      icon: PackageCheckIcon,
      className: 'border-teal-500/40 bg-teal-500/15 text-teal-200',
    },
    [Status.empty]: {
      label: 'Not started',
      icon: CircleMinusIcon,
      className: 'border-content-highlight/40 bg-content-dark/70 text-white/70',
    },
  };

  const statusBadge = (status: Status) => {
    const key = (status ?? Status.empty) as Exclude<Status, null>;
    const detail = STATUS_DETAILS[key];
    const Icon = detail.icon;
    return (
      <span
        className={cn(
          'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em]',
          detail.className,
        )}
      >
        <Icon className="h-4 w-4" />
        {detail.label}
      </span>
    );
  };

  const formatDate = (value?: string | null) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '—';
    return format(parsed, 'MMM d, yyyy');
  };

  const startIndex = totalElements === 0 ? 0 : pageable.page * pageable.size + 1;
  const endIndex = totalElements === 0 ? 0 : startIndex + rows.length - 1;
  const subtitle = totalElements
    ? `Showing ${startIndex}–${endIndex} of ${totalElements} patients`
    : 'No patients yet';

  const emptyState = (
    <div className="flex flex-col items-center gap-2 py-12 text-white/60">
      <CircleMinusIcon className="h-10 w-10 text-white/35" />
      <p className="text-sm">No patients match your filters. Try adjusting the search or add a new patient.</p>
    </div>
  );

  return (
    <ClickableTable<PatientRow>
      title="Patients"
      subtitle={subtitle}
      data={rows}
      detailRow={patientId ?? null}
      setDetailRow={(id: string) => {
        if (patientId === id) {
          navigate('/patients');
          return;
        }
        navigate(`/patients/${id}`);
      }}
      selectedRows={selectedPatients}
      includedColumns={['fullName', 'dateOfBirth', 'orthodontistName', 'latestScanStatus']}
      setSelectedRows={setSelectedPatients}
      columnNames={['Patient', 'Date of Birth', 'Orthodontist', 'Last Scan']}
      deleteSelectedRows={() => {
        navigate(`/patients/delete`, {
          state: { backgroundLocation: location },
        });
      }}
      editRow={(id: string) => {
        navigate(`/patients/${id}/edit`, { state: { backgroundLocation: location } });
      }}
      cellRenderers={{
        fullName: (_, row) => <span className="text-sm font-semibold text-white">{row.fullName}</span>,
        dateOfBirth: (value: string) => (
          <span className="text-sm text-white/80">{formatDate(value)}</span>
        ),
        orthodontistName: (value: string) => <span className="text-sm text-white/85">{value}</span>,
        latestScanStatus: (status: Status) => statusBadge(status),
      }}
      setPage={setPageable}
      actualPage={pageable.page}
      totalPages={tablePage ? tablePage.totalPages : 0}
      className={cn('w-full h-[75vh]', className)}
      emptyState={emptyState}
    />
  );
}
